import { useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALTCHA_CORE_URL = "/altcha/altcha.js";
const ALTCHA_I18N_URL = "/altcha/es-419.js";
const ALTCHA_STYLE_URL = "/altcha/altcha.css";
const ALTCHA_WORKER_SHA_URL = "/altcha/workers/sha.js";
const ALTCHA_WORKER_PBKDF2_URL = "/altcha/workers/pbkdf2.js";

function loadScriptOnce(key, src) {
  if (window[key]) {
    return window[key];
  }

  window[key] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-altcha-key="${key}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`No se pudo cargar ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.dataset.altchaKey = key;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.head.appendChild(script);
  });

  return window[key];
}

function loadStyleOnce(key, href) {
  if (window[key]) {
    return window[key];
  }

  window[key] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`link[data-altcha-key="${key}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`No se pudo cargar ${href}`)), {
        once: true,
      });
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.altchaKey = key;
    link.onload = () => {
      link.dataset.loaded = "true";
      resolve();
    };
    link.onerror = () => reject(new Error(`No se pudo cargar ${href}`));
    document.head.appendChild(link);
  });

  return window[key];
}

function registerAltchaAlgorithms() {
  const altcha = window.$altcha;
  if (!altcha?.algorithms) {
    return;
  }

  const createShaWorker = () => new Worker(ALTCHA_WORKER_SHA_URL, { name: "altcha-sha" });
  const createPbkdf2Worker = () => new Worker(ALTCHA_WORKER_PBKDF2_URL, { name: "altcha-pbkdf2" });

  if (!altcha.algorithms.get("SHA-256")) {
    altcha.algorithms.set("SHA-256", createShaWorker);
  }
  if (!altcha.algorithms.get("SHA-384")) {
    altcha.algorithms.set("SHA-384", createShaWorker);
  }
  if (!altcha.algorithms.get("SHA-512")) {
    altcha.algorithms.set("SHA-512", createShaWorker);
  }
  if (!altcha.algorithms.get("PBKDF2/SHA-256")) {
    altcha.algorithms.set("PBKDF2/SHA-256", createPbkdf2Worker);
  }
  if (!altcha.algorithms.get("PBKDF2/SHA-384")) {
    altcha.algorithms.set("PBKDF2/SHA-384", createPbkdf2Worker);
  }
  if (!altcha.algorithms.get("PBKDF2/SHA-512")) {
    altcha.algorithms.set("PBKDF2/SHA-512", createPbkdf2Worker);
  }
}

/**
 * Widget anti-bot ALTCHA (Proof-of-Work, self-hosted, sin tracking).
 * El usuario marca manualmente la casilla "No soy un robot" para resolver el challenge.
 * Idioma: español latinoamericano (es-419).
 */
export default function AltchaWidget({ onVerified, onExpired, testid = "altcha-widget" }) {
  const widgetRef = useRef(null);

  useEffect(() => {
    loadStyleOnce("__altchaStylePromise", ALTCHA_STYLE_URL)
      .then(() => loadScriptOnce("__altchaCorePromise", ALTCHA_CORE_URL))
      .then(() => loadScriptOnce("__altchaI18nEs419Promise", ALTCHA_I18N_URL))
      .then(() => registerAltchaAlgorithms())
      .catch((error) => {
        console.error("ALTCHA no pudo cargar recursos:", error);
      });

    const el = widgetRef.current;
    if (!el) return;

    const handleStateChange = (ev) => {
      const state = ev?.detail?.state;
      const payload = ev?.detail?.payload;
      if (state === "verified" && payload && onVerified) {
        onVerified(payload);
      }
      if (state === "expired" && onExpired) {
        onExpired();
      }
    };

    el.addEventListener("statechange", handleStateChange);
    return () => el.removeEventListener("statechange", handleStateChange);
  }, [onVerified, onExpired]);

  return (
    <div data-testid={testid} className="altcha-wrap">
      <altcha-widget
        ref={widgetRef}
        challenge={`${BACKEND_URL}/api/altcha/challenge`}
        language="es-419"
      />
    </div>
  );
}
